import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, TextField, InputLabel, Button, Typography, Divider, FormHelperText, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import { logoutClear, updateUserProfile, uploadImage, patchProfilePictureInStore } from "../../redux/actions/appActions";
import { useNavigate } from "react-router-dom";
import { ROUTE_MAIN } from "../../constants/routes";

// Validation Schema
const schema = yup.object().shape({
  fullName: yup.string().required("Full Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup
    .string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
});

const ProfileSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, setSelectedFile] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const user = useSelector((state: any) => state.app.user);
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);
  const { fullName, phoneNumber, email, profileImage } = user?.user || {};

  const defaultValues = { fullName: fullName || "", email: email || "", phoneNumber: phoneNumber || "" };

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [user]);

  const onSubmit = (data: any) => {
    dispatch(updateUserProfile(data));
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    dispatch(logoutClear(()=>{
     navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      uploadFile(file);
    }
  };

  const uploadFile = async (file: any) => {
    const res: any = await new Promise((resolve) => {
      dispatch(
        uploadImage(file, (result) => {
          resolve(result);
        })
      );
    });

    const pictureUrl = res?.result;
    if (pictureUrl) {
      dispatch(patchProfilePictureInStore(pictureUrl));
    }

    dispatch(
      updateUserProfile(
        { profilePicturePath: pictureUrl },
        () => {},
        () => {}
      )
    );
  };

  return (
    <Box
      sx={{ backgroundColor: "#ffffff", height: "100%", display: "flex", flexDirection: "column", alignItems: "left" }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            paddingLeft: "50px",
            gap: "10%",
            alignItems: "flex-end",
            marginTop: "20px",
          }}
        >
          <Box sx={{ width: "50%" }}>
            <div className="avatar-upload">
              <div className="avatar-edit">
                <input
                  capture={false}
                  type="file"
                  id="imageUpload"
                  onChange={handleFileChange}
                  accept=".png, .jpg, .jpeg"
                />
                <label htmlFor="imageUpload">
                  <EditIcon style={{ fontSize: 20, color: "#827BEA" }} />
                </label>
              </div>
              <div className="avatar-preview">
                <div id="imagePreview" style={{ backgroundImage: `url(${profileImage || user?.user?.profilePicturePath})` }}></div>
              </div>
            </div>

            <InputLabel
              id="fullName-label"
              shrink={true}
              sx={{ position: "relative", top: "10px", fontSize: "14px", color: "#1F2235", fontWeight: "500" }}
            >
              Full Name
            </InputLabel>
            <Controller
              name="fullName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                      borderRadius: "95px",
                      color: "#777777",
                    }}
                    placeholder="Saumya Sood"
                    error={!!errors.fullName}
                  />
                  <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                    {error ? error?.message : " "}
                  </FormHelperText>
                </>
              )}
            />

            <InputLabel
              id="email-label"
              shrink={true}
              sx={{ position: "relative", top: "10px", fontSize: "14px", color: "#1F2235", fontWeight: "500" }}
            >
              Registered Email ID
            </InputLabel>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    disabled
                    sx={{
                      boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                      borderRadius: "95px",
                      color: "#777777",
                    }}
                    placeholder="abc.saumya@gmail.com"
                    error={!!errors.email}
                  />
                  <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                    {error ? error?.message : " "}
                  </FormHelperText>
                </>
              )}
            />

            <InputLabel
              id="phoneNumber-label"
              shrink={true}
              sx={{ position: "relative", top: "10px", fontSize: "14px", color: "#1F2235", fontWeight: "500" }}
            >
              Mobile Number
            </InputLabel>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextField
                    {...field}
                    fullWidth
                    disabled
                    sx={{
                      boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                      borderRadius: "95px",
                      color: "#777777",
                    }}
                    placeholder="9849 9899 989"
                    error={!!errors.phoneNumber}
                  />
                  <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                    {error ? error?.message : " "}
                  </FormHelperText>
                </>
              )}
            />
            <Box sx={{ display: "flex", marginTop: "20px", gap: "20px" }}>
              <Button
                type="submit"
                variant="outlined"
                sx={{
                  backgroundColor: "#756DE9",
                  color: "#fff",
                  borderRadius: "100px",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: "#5a4dbf",
                    color: "#fff",
                    borderColor: "#5a4dbf",
                  },
                }}
                disabled={!!ajaxCallsInProgress}
              >
                Save Changes
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              backgroundColor: "#F5F6FA",
              padding: "20px",
              height: "30%",
              borderRadius: "10px",
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#222222" }}>Account Settings</Typography>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Button
                onClick={handleLogoutClick}
                sx={{ color: "#D1412A", fontSize: "18px", fontWeight: "500", textTransform: "capitalize" }}
              >
                Log Out
              </Button>
              <Divider sx={{ border: "1px solid #9F9F9F", height: "15px", margin: "14px 10px 0px 10px" }} />
              <Button sx={{ color: "#777777", fontSize: "18px", fontWeight: "500", textTransform: "capitalize" }}>
                Want to delete account?
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
          }
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{
            color: '#f44336',
            fontWeight: 600,
            fontSize: '1.25rem',
            textAlign: 'center',
          }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography
            id="logout-dialog-description"
            sx={{
              textAlign: 'center',
              color: '#666',
              fontSize: '1rem',
              padding: '8px 0',
            }}
          >
            Are you sure you want to logout?
            <br />
            You will need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, padding: '16px 24px' }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{
              borderColor: '#756DE9',
              color: '#756DE9',
              '&:hover': {
                borderColor: '#5a52d5',
                backgroundColor: 'rgba(117, 109, 233, 0.08)',
              },
              borderRadius: 2,
              paddingX: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#f44336',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
              borderRadius: 2,
              paddingX: 3,
              minWidth: 100,
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileSettings;
